import { ExternalLink } from 'lucide-react'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import {
  LMG_CONSUMER_PRIVACY_HTML,
  LMG_CONSUMER_PRIVACY_LAST_UPDATED,
  LMG_CONSUMER_PRIVACY_SOURCE,
} from '../content/lmgPrivacyConsumer'

/**
 * PrivacyPage — footer-only, regulatory.
 *
 * Greystone Finance is an authorised credit representative under LMG (Australian
 * Credit Licence 517192) and adopts LMG's Consumer Privacy Policy. Chelston Alvares
 * confirmed Greystone is bound by, and authorised to use, LMG's policy. The body is
 * reproduced verbatim from lmg.broker/privacy-policy-consumer (the CONSUMER policy —
 * for borrowers and the public — not the broker/franchisee policy). See
 * content/lmgPrivacyConsumer.ts.
 */

// Compliance copy — matches the footer ACL line; do not paraphrase.
const ACL_LINE =
  'Greystone Finance Pty Ltd ACN 668 602 846 is authorised under LMG Broker Services Pty Ltd ACN 632 405 504, Australian Credit Licence 517192.'

// Self-contained typography for the verbatim policy body (Tailwind preflight strips
// heading/list defaults). Scoped to this container only — no global CSS touched.
const POLICY_PROSE =
  'max-w-none ' +
  '[&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:text-ink [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-3 ' +
  '[&_p]:text-gs-text-soft [&_p]:leading-relaxed [&_p]:mb-4 ' +
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-2 [&_ul]:text-gs-text-soft ' +
  '[&_li]:leading-relaxed ' +
  '[&_a]:text-ink [&_a]:underline [&_a]:decoration-1 [&_a]:underline-offset-2 [&_a]:break-words ' +
  '[&_strong]:text-ink [&_strong]:font-semibold [&_em]:italic'

export default function PrivacyPage() {
  return (
    <>
      <Seo
        title="Privacy Policy"
        path="/privacy"
        description="How your personal information is collected, used and protected. Greystone Finance adopts LMG's Consumer Privacy Policy under Australian Credit Licence 517192."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tenantConfig.accentColor }} />
            Regulatory · Privacy
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold leading-tight mb-4">
            Privacy policy
          </h1>
          <p className="text-lg text-gs-text-soft">
            Protecting your personal information matters to us. {tenantConfig.name} is an authorised
            credit representative of LMG and adopts LMG&apos;s Consumer Privacy Policy, reproduced in
            full below.
          </p>
        </section>

        {/* AGGREGATOR DISCLOSURE */}
        <section className="max-w-4xl mx-auto px-6 pb-10">
          <div className="rounded-2xl border border-[#E8E8E2] bg-[#FAFAF7] p-6 text-sm">
            <div className="font-heading font-semibold text-ink mb-2">
              Our aggregator and privacy policy
            </div>
            <p className="text-gs-text-soft mb-3">
              {ACL_LINE} As an authorised credit representative, {tenantConfig.name} is bound by, and
              adopts, LMG&apos;s Consumer Privacy Policy. The full policy (governing how your personal
              information is collected, used, disclosed and protected) is set out below.
            </p>
            <a
              href={LMG_CONSUMER_PRIVACY_SOURCE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-ink underline decoration-1 underline-offset-2 break-words"
            >
              <ExternalLink size={13} /> View the current version at lmg.broker
            </a>
          </div>
        </section>

        {/* VERBATIM LMG CONSUMER PRIVACY POLICY */}
        <section className="max-w-4xl mx-auto px-6">
          <div
            className={POLICY_PROSE}
            dangerouslySetInnerHTML={{ __html: LMG_CONSUMER_PRIVACY_HTML }}
          />
          <p className="mt-8 text-xs text-gs-text-muted">
            Reproduced verbatim from LMG&apos;s Consumer Privacy Policy (last updated{' '}
            {LMG_CONSUMER_PRIVACY_LAST_UPDATED}). The authoritative, current version is maintained by
            LMG at{' '}
            <a
              href={LMG_CONSUMER_PRIVACY_SOURCE}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-1 underline-offset-2"
            >
              lmg.broker/privacy-policy-consumer
            </a>
            .
          </p>
        </section>
      </main>
      <Footer />
    </>
  )
}
